import React from 'react';
import { Card as AntCard, CardProps as AntCardProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

const useStyles = createUseStyles((theme: ThemeType) => ({
  card: {
    '&.ant-card': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      borderRadius: theme.radius.sm,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
    },
    '& .ant-card-head': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      padding: `${theme.spacing[0]} ${theme.spacing[4]}`,
      minHeight: 'auto',
      fontSize: theme.typography.fontSize.md,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.color.semantic.text.primary,
    },
    '& .ant-card-head-title': {
      padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
    },
    '& .ant-card-extra': {
      padding: `${theme.spacing[3]} ${theme.spacing[0]}`,
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
    },
    '& .ant-card-body': {
      padding: theme.spacing[4],
      fontSize: theme.typography.fontSize.sm,
      color: theme.color.semantic.text.primary,
    },
    '& .ant-card-actions': {
      borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      '& > li': {
        color: theme.color.semantic.text.secondary,
        '&:not(:last-child)': {
          borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        },
      },
    },
    '& .ant-card-cover img': {
      borderTopLeftRadius: theme.radius.sm,
      borderTopRightRadius: theme.radius.sm,
    },
    '& .ant-card-meta-title': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.md,
      fontWeight: theme.typography.fontWeight.semibold,
    },
    '& .ant-card-meta-description': {
      color: theme.color.semantic.text.secondary,
      fontSize: theme.typography.fontSize.sm,
    },
  },
  cardBorderless: {
    '&.ant-card': {
      borderWidth: theme.border.width[0],
    },
  },
  cardHoverable: {
    '&.ant-card-hoverable:hover': {
      borderColor: theme.color.semantic.brand.default.primary,
      boxShadow: theme.shadow[2],
    },
  },
  cardLoading: {
    '& .ant-card-loading-content p': {
      backgroundColor: theme.color.semantic.background.neutral.primary,
    },
  },
  cardInner: {
    '&.ant-card-type-inner .ant-card-head': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
    },
  },
  cardSmall: {
    '& .ant-card-head': {
      fontSize: theme.typography.fontSize.sm,
      padding: `${theme.spacing[0]} ${theme.spacing[3]}`,
    },
    '& .ant-card-head-title': {
      padding: `${theme.spacing[2]} ${theme.spacing[0]}`,
    },
    '& .ant-card-extra': {
      padding: `${theme.spacing[2]} ${theme.spacing[0]}`,
    },
    '& .ant-card-body': {
      padding: theme.spacing[3],
    },
  },
}));

const CardInner: React.FC<AntCardProps> = ({
  bordered = true,
  hoverable = false,
  loading = false,
  size,
  type,
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const combinedClassName = [
    classes.card,
    !bordered ? classes.cardBorderless : '',
    hoverable ? classes.cardHoverable : '',
    loading ? classes.cardLoading : '',
    type === 'inner' ? classes.cardInner : '',
    size === 'small' ? classes.cardSmall : '',
    className,
  ].filter(Boolean).join(' ').trim();

  return (
    <AntCard
      bordered={bordered}
      hoverable={hoverable}
      loading={loading}
      size={size}
      type={type}
      {...rest}
      className={combinedClassName}
    />
  );
};

export const Card = Object.assign(CardInner, {
  Grid: AntCard.Grid,
  Meta: AntCard.Meta,
});

export default Card;
